import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiUploadCloud } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { apiUrl, fileUrl } from '../utils/api';
import { buildSafeUploadFileName } from '../utils/fileNames';
import InlineLoader from './InlineLoader';
import {
  createBatchEntryFromSample,
  getLatestResultForSample,
  normalizeBatchEntryValue,
  shouldSaveBatchEntry,
} from './resultadoBatchUtils';

const RESULT_ATTACHMENT_ACCEPT = '.png,.jpg,.jpeg,.webp,.bmp,.gif,.svg,.jfif,.pdf';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Token ${token}` } : {};
};

const autoResizeTextarea = (textarea) => {
  if (!textarea) {
    return;
  }

  textarea.style.height = 'auto';
  textarea.style.height = `${textarea.scrollHeight}px`;
};

const getAttachmentName = (path = '') => {
  if (!path) {
    return 'archivo';
  }

  const cleanPath = path.split('?')[0];
  const segments = cleanPath.split('/');
  return segments[segments.length - 1] || 'archivo';
};

const ResultAttachmentPicker = ({
  existingFilePath = '',
  helperText,
  inputId,
  inputKey,
  onChange,
  selectedFileName = '',
}) => (
  <div className="result-upload-field">
    <input
      id={inputId}
      key={inputKey}
      type="file"
      className="result-upload-field__input"
      accept={RESULT_ATTACHMENT_ACCEPT}
      onChange={onChange}
    />
    <label htmlFor={inputId} className="result-upload-field__button">
      <span className="result-upload-field__button-icon">
        <FiUploadCloud />
      </span>
      <span className="result-upload-field__button-copy">
        <strong>{selectedFileName ? 'Cambiar imagen o PDF' : 'Seleccionar imagen o PDF de esta muestra'}</strong>
        <small>{selectedFileName || 'Haz clic aqui para adjuntar evidencia del resultado.'}</small>
      </span>
    </label>
    <div className="form-text mt-2">{helperText}</div>

    {selectedFileName ? (
      <div className="alert subtle-alert subtle-alert--info mt-3 mb-0">
        <FiUploadCloud /> Nuevo archivo seleccionado: {selectedFileName}
      </div>
    ) : null}

    {existingFilePath ? (
      <div className="alert subtle-alert subtle-alert--info mt-3 mb-0">
        Adjunto actual:{' '}
        <a href={fileUrl(existingFilePath)} target="_blank" rel="noreferrer">
          {getAttachmentName(existingFilePath)}
        </a>
      </div>
    ) : null}
  </div>
);

const ResultadoDiagnosticoForm = ({
  protocoloMuestraInicial = '',
  protocoloGeneralInicial = '',
  resultId = null,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isEditMode = Boolean(resultId);
  const returnToCaseDetail = Boolean(location.state?.returnToCaseDetail || isEditMode);
  const [tipoAnalisis, setTipoAnalisis] = useState('');
  const [resultadoTexto, setResultadoTexto] = useState('');
  const [muestras, setMuestras] = useState([]);
  const [protocolos, setProtocolos] = useState([]);
  const [protocoloMuestra, setProtocoloMuestra] = useState(protocoloMuestraInicial);
  const [protocoloSeleccionado, setProtocoloSeleccionado] = useState(protocoloGeneralInicial);
  const [batchEntries, setBatchEntries] = useState({});
  const [loadingProtocols, setLoadingProtocols] = useState(true);
  const [loadingSamples, setLoadingSamples] = useState(false);
  const [loadingResult, setLoadingResult] = useState(isEditMode);
  const [savingBatch, setSavingBatch] = useState(false);
  const [saveNotice, setSaveNotice] = useState(null);
  const [editingResult, setEditingResult] = useState(null);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [batchFocusSample, setBatchFocusSample] = useState(protocoloMuestraInicial);
  const resultadoTextareaRef = useRef(null);

  useEffect(() => {
    const fetchProtocolos = async () => {
      setLoadingProtocols(true);
      try {
        const casosRes = await axios.get(apiUrl('/api/ingresos_casos/?compact=1'));
        setProtocolos(casosRes.data || []);
      } catch (error) {
        toast.error('Error al cargar protocolos');
        console.error('Detalles del error:', error.response?.data || error);
      } finally {
        setLoadingProtocols(false);
      }
    };

    fetchProtocolos();
  }, []);

  useEffect(() => {
    if (!protocoloSeleccionado) {
      setMuestras([]);
      return;
    }

    let active = true;

    const fetchMuestras = async () => {
      setLoadingSamples(true);
      try {
        const response = await axios.get(
          apiUrl(`/api/ingresos_muestras/?protocolo_general=${encodeURIComponent(protocoloSeleccionado)}`)
        );

        if (!active) {
          return;
        }

        setMuestras(response.data || []);
      } catch (error) {
        if (!active) {
          return;
        }

        setMuestras([]);
        toast.error('Error al cargar las muestras del protocolo');
        console.error('Detalles del error:', error.response?.data || error);
      } finally {
        if (active) {
          setLoadingSamples(false);
        }
      }
    };

    fetchMuestras();

    return () => {
      active = false;
    };
  }, [protocoloSeleccionado]);

  useEffect(() => {
    if (!isEditMode) {
      setLoadingResult(false);
      return;
    }

    let active = true;
    setLoadingResult(true);

    axios
      .get(apiUrl(`/api/resultados/${resultId}/`), {
        headers: getAuthHeaders(),
      })
      .then((response) => {
        if (!active) {
          return;
        }

        const result = response.data || {};
        setEditingResult(result);
        setProtocoloMuestra(result.muestra || '');
        setTipoAnalisis(result.tipo_analisis || '');
        setResultadoTexto(result.resultado_texto || '');
        setSelectedAttachment(null);
      })
      .catch((error) => {
        console.error('Error al cargar el resultado:', error);
        if (!active) {
          return;
        }

        const message = 'No se pudo cargar el resultado para editar.';
        setSaveNotice({ type: 'error', text: message });
        toast.error(message);
      })
      .finally(() => {
        if (active) {
          setLoadingResult(false);
        }
      });

    return () => {
      active = false;
    };
  }, [isEditMode, resultId]);

  useEffect(() => {
    if (isEditMode && editingResult && muestras.length > 0) {
      const muestraEditada = muestras.find((muestra) => muestra.protocolo_muestra === editingResult.muestra);
      if (muestraEditada) {
        setProtocoloSeleccionado(muestraEditada.protocolo_general || '');
      }
      return;
    }

    if (protocoloGeneralInicial) {
      setProtocoloSeleccionado(protocoloGeneralInicial);
    }
  }, [editingResult, isEditMode, muestras, protocoloGeneralInicial]);

  useEffect(() => {
    if (!protocoloMuestraInicial || isEditMode || !muestras.length) {
      return;
    }

    const muestraInicial = muestras.find((muestra) => muestra.protocolo_muestra === protocoloMuestraInicial);
    if (!muestraInicial) {
      return;
    }

    setProtocoloMuestra(muestraInicial.protocolo_muestra);
    setProtocoloSeleccionado(muestraInicial.protocolo_general || '');
    setBatchFocusSample(muestraInicial.protocolo_muestra);
  }, [isEditMode, muestras, protocoloMuestraInicial]);

  const muestrasFiltradas = useMemo(
    () => muestras.filter((muestra) => muestra.protocolo_general === protocoloSeleccionado),
    [muestras, protocoloSeleccionado]
  );

  const muestraSeleccionada = useMemo(
    () => muestras.find((muestra) => muestra.protocolo_muestra === protocoloMuestra),
    [muestras, protocoloMuestra]
  );

  useEffect(() => {
    if (isEditMode) {
      return;
    }

    setBatchEntries((current) => {
      const nextEntries = {};

      muestrasFiltradas.forEach((muestra) => {
        nextEntries[muestra.protocolo_muestra] = current[muestra.protocolo_muestra] || createBatchEntryFromSample(muestra);
      });

      return nextEntries;
    });
  }, [isEditMode, muestrasFiltradas]);

  useEffect(() => {
    if (isEditMode) {
      autoResizeTextarea(resultadoTextareaRef.current);
    }
  }, [isEditMode, resultadoTexto]);

  const sectorMuestraSeleccionada = muestraSeleccionada?.sector || '';
  const singleAttachmentInputId = `result-attachment-${resultId || protocoloMuestra || protocoloGeneralInicial || 'nuevo'}`;

  const muestrasConResultados = useMemo(
    () => muestrasFiltradas.filter((muestra) => Array.isArray(muestra.resultados) && muestra.resultados.length > 0).length,
    [muestrasFiltradas]
  );

  const muestrasCompletadas = useMemo(
    () =>
      muestrasFiltradas.filter((muestra) => {
        const entry = batchEntries[muestra.protocolo_muestra];
        return shouldSaveBatchEntry(muestra, entry);
      }).length,
    [batchEntries, muestrasFiltradas]
  );

  const handleProtocoloChange = (event) => {
    const nuevoProtocolo = event.target.value;
    setProtocoloSeleccionado(nuevoProtocolo);
    setProtocoloMuestra('');
    setSaveNotice(null);
  };

  const handleBatchEntryChange = (protocolo, field, value) => {
    setBatchEntries((current) => ({
      ...current,
      [protocolo]: {
        ...(current[protocolo] || createBatchEntryFromSample(muestras.find((muestra) => muestra.protocolo_muestra === protocolo))),
        [field]: value,
      },
    }));
  };

  const handleSingleAttachmentChange = (event) => {
    setSelectedAttachment(event.target.files?.[0] || null);
  };

  const handleSingleSubmit = async (event) => {
    event.preventDefault();
    setSaveNotice(null);

    if (!protocoloMuestra) {
      toast.error('Debes seleccionar una muestra');
      setSaveNotice({ type: 'error', text: 'Debes seleccionar una muestra antes de guardar.' });
      return;
    }

    if (!sectorMuestraSeleccionada) {
      toast.error('La muestra seleccionada no tiene sector asignado.');
      setSaveNotice({
        type: 'error',
        text: 'La muestra seleccionada no tiene sector asignado. Revisa la carga de la muestra antes de guardar el resultado.',
      });
      return;
    }

    const formData = new FormData();
    formData.append('muestra', protocoloMuestra);
    formData.append('sector', sectorMuestraSeleccionada);
    formData.append('tipo_analisis', tipoAnalisis);
    formData.append('resultado_texto', resultadoTexto);
    if (selectedAttachment) {
      formData.append('archivo_resultado', selectedAttachment, buildSafeUploadFileName(selectedAttachment.name));
    }

    try {
      const response = isEditMode
        ? await axios.put(apiUrl(`/api/resultados/${resultId}/`), formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              ...getAuthHeaders(),
            },
          })
        : await axios.post(apiUrl('/api/resultados/'), formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              ...getAuthHeaders(),
            },
          });

      const successText = isEditMode ? 'Resultado actualizado correctamente.' : 'Resultado guardado correctamente.';
      const resultIdentifier = response.data?.id || resultId;

      toast.success(successText);
      setSaveNotice({ type: 'success', text: successText });
      setSelectedAttachment(null);

      if (protocoloSeleccionado && returnToCaseDetail) {
        navigate(`/ver-caso/${protocoloSeleccionado}`, {
          state: isEditMode
            ? { muestraActiva: protocoloMuestra, resultadoActivo: resultIdentifier, resultadoActualizado: resultIdentifier }
            : { muestraActiva: protocoloMuestra, resultadoActivo: resultIdentifier, resultadoRecienCreado: resultIdentifier },
        });
        return;
      }

      if (!isEditMode) {
        setTipoAnalisis('');
        setResultadoTexto('');
        setProtocoloMuestra('');
      }
    } catch (error) {
      toast.error(isEditMode ? 'Error al actualizar el resultado' : 'Error al guardar el resultado');
      console.error('Detalles del error:', error.response?.data || error);
      setSaveNotice({
        type: 'error',
        text: isEditMode
          ? 'No se pudo actualizar el resultado. Revisa los datos e intenta nuevamente.'
          : 'No se pudo guardar el resultado. Revisa los datos e intenta nuevamente.',
      });
    }
  };

  const handleBatchSubmit = async (event) => {
    event.preventDefault();
    setSaveNotice(null);

    if (!protocoloSeleccionado) {
      const message = 'Selecciona un protocolo general antes de guardar resultados.';
      toast.error(message);
      setSaveNotice({ type: 'error', text: message });
      return;
    }

    if (muestrasFiltradas.length === 0) {
      const message = 'El protocolo seleccionado no tiene muestras para cargar.';
      toast.error(message);
      setSaveNotice({ type: 'error', text: message });
      return;
    }

    const entradasParciales = muestrasFiltradas
      .map((muestra) => {
        const entry = batchEntries[muestra.protocolo_muestra] || createBatchEntryFromSample(muestra);
        const hasTipo = Boolean(entry.tipo_analisis.trim());
        const hasResultado = Boolean(entry.resultado_texto.trim());
        const hasAttachment = Boolean(entry.archivo_resultado);
        return hasTipo !== hasResultado || (hasAttachment && (!hasTipo || !hasResultado))
          ? muestra.protocolo_muestra
          : null;
      })
      .filter(Boolean);

    if (entradasParciales.length > 0) {
      const message = `Completa tipo de analisis y resultado en las muestras: ${entradasParciales.join(', ')}.`;
      toast.error(message);
      setSaveNotice({ type: 'error', text: message });
      return;
    }

    const muestrasParaGuardar = muestrasFiltradas.filter((muestra) => {
      const entry = batchEntries[muestra.protocolo_muestra] || createBatchEntryFromSample(muestra);
      return shouldSaveBatchEntry(muestra, entry);
    });

    if (muestrasParaGuardar.length === 0) {
      const message = 'Modifica un resultado precargado, adjunta un archivo o completa una muestra sin resultados antes de guardar.';
      toast.error(message);
      setSaveNotice({ type: 'error', text: message });
      return;
    }

    const muestrasSinSector = muestrasParaGuardar.filter((muestra) => !muestra.sector);
    if (muestrasSinSector.length > 0) {
      const message = `Estas muestras no tienen sector asignado: ${muestrasSinSector
        .map((muestra) => muestra.protocolo_muestra)
        .join(', ')}.`;
      toast.error(message);
      setSaveNotice({ type: 'error', text: message });
      return;
    }

    setSavingBatch(true);

    try {
      const responses = await Promise.allSettled(
        muestrasParaGuardar.map(async (muestra) => {
          const entry = batchEntries[muestra.protocolo_muestra];
          const formData = new FormData();
          formData.append('muestra', muestra.protocolo_muestra);
          formData.append('sector', muestra.sector || '');
          formData.append('tipo_analisis', normalizeBatchEntryValue(entry?.tipo_analisis));
          formData.append('resultado_texto', normalizeBatchEntryValue(entry?.resultado_texto));
          if (entry?.archivo_resultado) {
            formData.append(
              'archivo_resultado',
              entry.archivo_resultado,
              buildSafeUploadFileName(entry.archivo_resultado.name)
            );
          }

          const response = await axios.post(apiUrl('/api/resultados/'), formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              ...getAuthHeaders(),
            },
          });

          return {
            muestra,
            data: response.data,
          };
        })
      );

      const exitosos = responses
        .filter((item) => item.status === 'fulfilled')
        .map((item) => item.value);
      const fallidos = responses
        .map((item, index) => (item.status === 'rejected' ? muestrasParaGuardar[index].protocolo_muestra : null))
        .filter(Boolean);

      if (exitosos.length > 0) {
        setMuestras((current) =>
          current.map((muestra) => {
            const match = exitosos.find((item) => item.muestra.protocolo_muestra === muestra.protocolo_muestra);
            if (!match) {
              return muestra;
            }

            return {
              ...muestra,
              resultados: [...(muestra.resultados || []), match.data],
            };
          })
        );

        setBatchEntries((current) => {
          const next = { ...current };
          exitosos.forEach(({ muestra }) => {
            const muestraActualizada = {
              ...muestra,
              resultados: [...(muestra.resultados || []), exitosos.find((item) => item.muestra.protocolo_muestra === muestra.protocolo_muestra)?.data].filter(Boolean),
            };
            next[muestra.protocolo_muestra] = createBatchEntryFromSample(muestraActualizada);
          });
          return next;
        });
      }

      if (fallidos.length === 0) {
        const message =
          exitosos.length === 1
            ? 'Se guardó 1 resultado correctamente.'
            : `Se guardaron ${exitosos.length} resultados correctamente.`;
        toast.success(message);
        setSaveNotice({ type: 'success', text: message });

        if (returnToCaseDetail) {
          navigate(`/ver-caso/${protocoloSeleccionado}`, {
            state: {
              muestraActiva: exitosos[0]?.muestra?.protocolo_muestra || batchFocusSample || '',
              resultadoActivo: exitosos[0]?.data?.id || '',
              resultadosCreadosCantidad: exitosos.length,
            },
          });
        }
        return;
      }

      const message =
        exitosos.length > 0
          ? `Se guardaron ${exitosos.length} resultados, pero fallaron estas muestras: ${fallidos.join(', ')}.`
          : `No se pudo guardar ningún resultado. Revisa estas muestras: ${fallidos.join(', ')}.`;
      toast[exitosos.length > 0 ? 'warn' : 'error'](message);
      setSaveNotice({ type: exitosos.length > 0 ? 'success' : 'error', text: message });
    } catch (error) {
      toast.error('No se pudieron guardar los resultados.');
      console.error('Detalles del error:', error.response?.data || error);
      setSaveNotice({
        type: 'error',
        text: 'No se pudieron guardar los resultados. Revisa los datos e intenta nuevamente.',
      });
    } finally {
      setSavingBatch(false);
    }
  };

  if (loadingProtocols || loadingResult) {
    return (
      <div className="surface-card">
        <InlineLoader label={loadingResult ? 'Cargando resultado para editar...' : 'Cargando protocolos...'} />
      </div>
    );
  }

  if (isEditMode) {
    return (
      <div className="surface-card">
        <div className="form-highlight mb-4">
          <div>
            <span className="section-kicker">Edición</span>
            <h3 className="form-title">Editar resultado por muestra</h3>
            <p className="page-subtitle mt-2 mb-0">
              Ajusta el análisis y regresa al detalle del caso para verificar el cambio.
            </p>
          </div>
          <div className="protocol-chip">
            {protocoloMuestra ? `Muestra ${protocoloMuestra}` : 'Pendiente de selección'}
          </div>
        </div>

        <div className="result-form-grid mb-4">
          <div className="result-form-grid__main">
            <form onSubmit={handleSingleSubmit}>
              {saveNotice ? (
                <div className={`alert ${saveNotice.type === 'success' ? 'alert-success' : 'subtle-alert'} mb-4`} role="status">
                  {saveNotice.text}
                </div>
              ) : null}

              <div className="mb-3">
                <label className="form-label">Protocolo general</label>
                <select className="form-select" value={protocoloSeleccionado} onChange={handleProtocoloChange} required>
                  <option value="">Seleccione un protocolo...</option>
                  {protocolos.map((protocolo) => (
                    <option key={protocolo.protocolo_general} value={protocolo.protocolo_general}>
                      {protocolo.protocolo_general}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label">Muestra</label>
                <select
                  className="form-select"
                  value={protocoloMuestra}
                  onChange={(event) => setProtocoloMuestra(event.target.value)}
                  disabled={!protocoloSeleccionado}
                  required
                >
                  <option value="">
                    {protocoloSeleccionado ? 'Seleccione una muestra...' : 'Primero elige un protocolo'}
                  </option>
                  {muestrasFiltradas.map((muestra) => (
                    <option key={muestra.protocolo_muestra} value={muestra.protocolo_muestra}>
                      {muestra.protocolo_muestra}
                    </option>
                  ))}
                </select>
                <div className="form-text mt-2">
                  Solo se muestran las muestras asociadas al protocolo general seleccionado.
                </div>
              </div>

              {loadingSamples ? (
                <div className="mb-4">
                  <InlineLoader label="Cargando muestras del protocolo..." />
                </div>
              ) : null}

              {protocoloSeleccionado && !loadingSamples && muestrasFiltradas.length === 0 ? (
                <div className="alert subtle-alert d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-4">
                  <div>
                    No hay muestras asociadas al protocolo <strong>{protocoloSeleccionado}</strong>.
                    Registra una muestra para poder cargar el resultado diagnóstico.
                  </div>
                  <Link
                    to="/ingreso-muestra"
                    state={{ protocoloGeneral: protocoloSeleccionado, returnToCaseDetail: true }}
                    className="btn btn-outline-brand"
                  >
                    Agregar muestra
                  </Link>
                </div>
              ) : null}

              <div className="mb-3">
                <label className="form-label">Sector</label>
                <div className="readonly-field">
                  <strong className="readonly-field__value">
                    {sectorMuestraSeleccionada || 'Selecciona una muestra para ver su sector'}
                  </strong>
                </div>
                <div className="form-text mt-2">
                  El sector se toma automáticamente desde la muestra seleccionada.
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Tipo de análisis</label>
                <input
                  type="text"
                  className="form-control"
                  value={tipoAnalisis}
                  onChange={(event) => setTipoAnalisis(event.target.value)}
                  placeholder="Ej. Cultivo bacteriológico"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Resultado</label>
                <textarea
                  ref={resultadoTextareaRef}
                  className="form-control result-form__textarea"
                  value={resultadoTexto}
                  onChange={(event) => setResultadoTexto(event.target.value)}
                  rows="7"
                  placeholder="Resume hallazgos, conclusión e información relevante específicamente sobre la muestra tomada."
                  required
                />
              </div>

              <div className="mb-4">
                <label className="form-label">Adjunto del resultado</label>
                <ResultAttachmentPicker
                  existingFilePath={editingResult?.archivo_adjunto || ''}
                  helperText="Puedes adjuntar una imagen diagnostica o un PDF complementario para este resultado."
                  inputId={singleAttachmentInputId}
                  inputKey={selectedAttachment?.name || editingResult?.archivo_adjunto || 'sin-adjunto'}
                  onChange={handleSingleAttachmentChange}
                  selectedFileName={selectedAttachment?.name || ''}
                />
              </div>

              <div className="d-flex justify-content-end pt-2">
                <button type="submit" className="btn btn-brand">
                  Guardar cambios del resultado
                </button>
              </div>
            </form>
          </div>

          <aside className="result-form-grid__aside">
            <div className="result-summary-card">
              <span className="section-kicker">Resumen</span>
              <h4 className="result-summary-card__title">Antes de guardar</h4>
              <p className="result-summary-card__text">
                Revisa que el resultado quede asociado a la muestra correcta para evitar cargas duplicadas o mal vinculadas.
              </p>

              <dl className="result-summary-list">
                <div>
                  <dt>Protocolo general</dt>
                  <dd>{protocoloSeleccionado || 'Sin seleccionar'}</dd>
                </div>
                <div>
                  <dt>Muestra</dt>
                  <dd>{protocoloMuestra || 'Sin seleccionar'}</dd>
                </div>
                <div>
                  <dt>Sector</dt>
                  <dd>{sectorMuestraSeleccionada || 'Pendiente'}</dd>
                </div>
                <div>
                  <dt>Tipo de análisis</dt>
                  <dd>{tipoAnalisis || 'Pendiente'}</dd>
                </div>
              </dl>

              {muestraSeleccionada ? (
                <div className="alert subtle-alert subtle-alert--info mt-3 mb-0">
                  La muestra <strong>{muestraSeleccionada.protocolo_muestra}</strong> pertenece al protocolo{' '}
                  <strong>{muestraSeleccionada.protocolo_general}</strong>.
                </div>
              ) : null}
            </div>
          </aside>
        </div>
      </div>
    );
  }

  return (
    <div className="surface-card">
      <div className="form-highlight mb-4">
        <div>
          <span className="section-kicker">Paso 3</span>
          <h3 className="form-title">Registrar resultados por muestra</h3>
          <p className="page-subtitle mt-2 mb-0">
            Selecciona un protocolo general y aparecerán las muestras asociadas. Luego carga el resultado para la muestra correspondiente.
          </p>
        </div>
        <div className="protocol-chip">
          {protocoloSeleccionado ? `Caso ${protocoloSeleccionado}` : 'Sin caso seleccionado'}
        </div>
      </div>

      <div className="result-form-grid mb-4">
        <div className="result-form-grid__main">
          <form onSubmit={handleBatchSubmit}>
            {saveNotice ? (
              <div className={`alert ${saveNotice.type === 'error' ? 'subtle-alert' : 'alert-success'} mb-4`} role="status">
                {saveNotice.text}
              </div>
            ) : null}

            <div className="mb-4">
              <label className="form-label">Protocolo general</label>
              <select className="form-select" value={protocoloSeleccionado} onChange={handleProtocoloChange} required>
                <option value="">Seleccione un protocolo...</option>
                {protocolos.map((protocolo) => (
                  <option key={protocolo.protocolo_general} value={protocolo.protocolo_general}>
                    {protocolo.protocolo_general}
                  </option>
                ))}
              </select>
              <div className="form-text mt-2">
                Al elegir un protocolo se muestran todas las muestras asociadas para cargar sus resultados en la misma pantalla.
              </div>
            </div>

            {loadingSamples ? (
              <div className="mb-4">
                <InlineLoader label="Cargando muestras del protocolo..." />
              </div>
            ) : null}

            {protocoloSeleccionado && !loadingSamples && muestrasFiltradas.length === 0 ? (
              <div className="alert subtle-alert d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-4">
                <div>
                  No hay muestras asociadas al protocolo <strong>{protocoloSeleccionado}</strong>.
                  Registra una muestra para poder cargar resultados diagnósticos.
                </div>
                <Link
                  to="/ingreso-muestra"
                  state={{ protocoloGeneral: protocoloSeleccionado, returnToCaseDetail: true }}
                  className="btn btn-outline-brand"
                >
                  Agregar muestra
                </Link>
              </div>
            ) : null}

            {!loadingSamples && muestrasFiltradas.length > 0 ? (
              <div className="result-batch-list">
                {muestrasFiltradas.map((muestra) => {
                  const entry = batchEntries[muestra.protocolo_muestra] || createBatchEntryFromSample(muestra);
                  const hasExistingResults = Boolean(muestra.resultados?.length);
                  const latestResult = getLatestResultForSample(muestra);
                  const isHighlighted = batchFocusSample && muestra.protocolo_muestra === batchFocusSample;
                  const attachmentInputId = `batch-result-attachment-${muestra.protocolo_muestra}`;

                  return (
                    <section
                      key={muestra.protocolo_muestra}
                      className={`result-batch-item${isHighlighted ? ' result-batch-item--highlighted' : ''}`}
                    >
                      <div className="result-batch-item__protocol-wrap">
                        <span
                          className={`result-batch-item__protocol-chip ${
                            hasExistingResults
                              ? 'result-batch-item__protocol-chip--with-results'
                              : 'result-batch-item__protocol-chip--empty'
                          }`}
                        >
                          {muestra.protocolo_muestra}
                        </span>
                      </div>

                      <article className={`result-batch-card${isHighlighted ? ' result-batch-card--highlighted' : ''}`}>
                        <div className="result-batch-card__header">
                          <div>
                            <div className="result-batch-card__chips">
                              <span className="protocol-chip">{muestra.sector || 'Sin sector'}</span>
                              <span className="protocol-chip">
                                {muestra.resultados?.length || 0} resultado(s) previos
                              </span>
                            </div>
                            <h4 className="result-batch-card__title">
                              {muestra.tipo_muestra || 'Sin tipo de muestra'} / {muestra.grupo_zootecnico || 'Sin grupo'}
                            </h4>
                          </div>
                          {hasExistingResults ? (
                            <div className="result-batch-card__existing">
                              <strong>Último análisis</strong>
                              <span>{latestResult?.tipo_analisis || 'Sin detalle'}</span>
                              <small>Los campos se precargan con este último resultado y solo se guardan si los modificas.</small>
                            </div>
                          ) : (
                            <div className="result-batch-card__existing result-batch-card__existing--empty">
                              Sin resultados cargados todavía
                            </div>
                          )}
                        </div>

                        <div className="result-batch-card__meta">
                          <div className="readonly-field">
                            <strong className="readonly-field__value">
                              Sector: {muestra.sector || 'Asignar sector en la muestra'}
                            </strong>
                          </div>
                          {muestra.observaciones ? (
                            <div className="result-batch-card__note">
                              <strong>Observaciones de la muestra</strong>
                              <p>{muestra.observaciones}</p>
                            </div>
                          ) : null}
                        </div>

                        <div className="row g-3 mt-1">
                          <div className="col-12 col-lg-5">
                            <label className="form-label">Tipo de análisis</label>
                            <input
                              type="text"
                              className="form-control"
                              value={entry.tipo_analisis}
                              onChange={(event) =>
                                handleBatchEntryChange(muestra.protocolo_muestra, 'tipo_analisis', event.target.value)
                              }
                              placeholder="Ej. Cultivo bacteriológico"
                            />
                          </div>
                          <div className="col-12 col-lg-7">
                            <label className="form-label">Resultado</label>
                            <textarea
                              className="form-control result-form__textarea"
                              value={entry.resultado_texto}
                              onChange={(event) =>
                                handleBatchEntryChange(muestra.protocolo_muestra, 'resultado_texto', event.target.value)
                              }
                              onInput={(event) => autoResizeTextarea(event.currentTarget)}
                              rows="5"
                              placeholder={`Carga el resultado para la muestra ${muestra.protocolo_muestra}.`}
                            />
                          </div>
                          <div className="col-12">
                            <label className="form-label">Adjunto del resultado</label>
                            <ResultAttachmentPicker
                              existingFilePath={latestResult?.archivo_adjunto || ''}
                              helperText="Adjunta una imagen diagnostica o un PDF complementario para esta muestra."
                              inputId={attachmentInputId}
                              inputKey={`${muestra.protocolo_muestra}-${entry.archivo_resultado?.name || latestResult?.id || 'sin-adjunto'}`}
                              onChange={(event) =>
                                handleBatchEntryChange(
                                  muestra.protocolo_muestra,
                                  'archivo_resultado',
                                  event.target.files?.[0] || null
                                )
                              }
                              selectedFileName={entry.archivo_resultado?.name || ''}
                            />
                          </div>
                        </div>
                      </article>
                    </section>
                  );
                })}
              </div>
            ) : null}

            <div className="d-flex justify-content-end pt-3">
              <button type="submit" className="btn btn-brand" disabled={savingBatch || loadingSamples || !protocoloSeleccionado}>
                {savingBatch ? 'Guardando resultados...' : 'Guardar resultados cargados'}
              </button>
            </div>
          </form>
        </div>

        <aside className="result-form-grid__aside">
          <div className="result-summary-card">
            <span className="section-kicker">Resumen</span>
            <h4 className="result-summary-card__title">Carga por caso</h4>
            <p className="result-summary-card__text">
              Puedes completar una, varias o todas las muestras del protocolo y guardarlas juntas en un solo envío.
            </p>

            <dl className="result-summary-list">
              <div>
                <dt>Protocolo general</dt>
                <dd>{protocoloSeleccionado || 'Sin seleccionar'}</dd>
              </div>
              <div>
                <dt>Muestras del caso</dt>
                <dd>{muestrasFiltradas.length}</dd>
              </div>
              <div>
                <dt>Muestras completas</dt>
                <dd>{muestrasCompletadas}</dd>
              </div>
              <div>
                <dt>Con resultados previos</dt>
                <dd>{muestrasConResultados}</dd>
              </div>
            </dl>

            {batchFocusSample ? (
              <div className="alert subtle-alert subtle-alert--info mt-3 mb-0">
                La muestra <strong>{batchFocusSample}</strong> quedó resaltada para que puedas ubicarla más rápido.
              </div>
            ) : null}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default ResultadoDiagnosticoForm;
